import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { slug: 'tous', label: 'Tous', icon: '📋' },
  { slug: 'journalisme', label: 'Journalisme', icon: '📰' },
  { slug: 'communication', label: 'Communication', icon: '📣' },
  { slug: 'marketing', label: 'Marketing', icon: '📱' },
  { slug: 'influence', label: 'Influence', icon: '🌍' },
  { slug: 'innovation', label: 'Innovation', icon: '🔬' },
  { slug: 'formation', label: 'Formation', icon: '🎓' },
];

const BlogSubNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryCounts, setCategoryCounts] = useState({});

  const isBlogPage = location.pathname === '/blog' || location.pathname.startsWith('/blog/');

  useEffect(() => {
    if (!isBlogPage) return;
    axios.get(`${API_URL}/api/blog/categories`)
      .then(res => {
        const counts = {};
        (res.data || []).forEach(c => { counts[c.slug] = c.count; });
        setCategoryCounts(counts);
      })
      .catch(() => {});
  }, [isBlogPage]);

  // Return null AFTER all hooks
  if (!isBlogPage) return null;

  const pathAfterBlog = location.pathname.replace('/blog', '').replace(/^\//, '').split('/')[0] || '';
  // Don't treat "article" as a category
  const activeSlug = (pathAfterBlog && pathAfterBlog !== 'article') ? pathAfterBlog : 'tous';

  const handleCategoryClick = (slug) => {
    if (slug === 'tous') navigate('/blog');
    else navigate(`/blog/${slug}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/blog?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div className="w-full bg-[#fafafa] dark:bg-gray-900/50 border-t-[3px] border-t-[#e11d48] border-b border-b-gray-200 dark:border-b-gray-800 sticky top-16 z-[40] shadow-sm">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex items-center h-11 gap-0">
        {/* Blog logo */}
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-1.5 mr-4 flex-shrink-0"
        >
          <span className="text-sm">✍️</span>
          <span className="text-[13px] font-bold text-foreground tracking-tight">Blog</span>
          <span className="text-[9px] font-bold bg-[#e11d48] text-white px-1.5 py-px rounded tracking-wider">PRO</span>
        </button>

        {/* Separator */}
        <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mr-3 flex-shrink-0" />

        {/* Categories */}
        <div className="flex items-center gap-0.5 flex-1 overflow-x-auto min-w-0" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {CATEGORIES.map(cat => {
            const isActive = activeSlug === cat.slug;
            const count = categoryCounts[cat.slug];
            return (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 transition-all touch-manipulation ${
                  isActive
                    ? 'bg-[#e11d48] text-white'
                    : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <span className="text-xs">{cat.icon}</span>
                {cat.label}
                {count > 0 && (
                  <span className={`text-[10px] px-1 rounded-full min-w-[16px] text-center ${
                    isActive ? 'bg-white/25 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="flex items-center ml-2 flex-shrink-0">
          {searchOpen ? (
            <form onSubmit={handleSearch} className="flex items-center gap-1.5">
              <input
                autoFocus
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="px-3 py-1 border border-gray-200 dark:border-gray-700 rounded-full text-xs outline-none w-36 sm:w-40 bg-white dark:bg-gray-900 text-foreground focus:border-[#e11d48]"
              />
              <button type="button" onClick={() => setSearchOpen(false)} className="text-gray-400 hover:text-gray-600 text-sm px-1">
                ✕
              </button>
            </form>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-full text-xs text-gray-500 hover:border-[#e11d48] hover:text-[#e11d48] transition-colors"
            >
              🔍 <span className="hidden sm:inline">Rechercher</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogSubNav;
